import { EXEBOOT_BROADBAND_PLAN_IDS } from '@/constants/app.constants';
import type { AddressMatch } from '@/services/apis/addressSearch/schemas';
import { apiQueryKeys } from '@/services/apis/apiQueryKeys';
import type { UserService } from '@/services/apis/auth/auth.schemas';
import type { QueryClient } from '@tanstack/react-query';
import { SUPERLOOP_URL } from '@/constants/app.constants';
import type { ServiceProviderLowerCase } from '@/services/apis/getServiceQualification/schemas';

/**
 * Get a limited number of suggestions from the address search results to be consistent across the app
 * @param data - The address search results
 * @param limit - The maximum number of suggestions to return
 * @returns A limited number of suggestions from the address search results
 */
export const getLimitedAddressSuggestions = ({
  data,
  limit = 5,
}: {
  data: AddressMatch[] | undefined;
  limit?: number;
}): AddressMatch[] => {
  return data?.slice(0, limit) ?? [];
};

/**
 * Cache the selected address from the address search results in the query cache to avoid an extra request
 */
export const cacheSelectedAddress = ({
  queryClient,
  selectedAddress,
}: {
  queryClient: QueryClient;
  selectedAddress: AddressMatch;
}): void => {
  const fullAddressSearch = selectedAddress.formattedAddress;
  const queryKey = [...apiQueryKeys.addressSearch, fullAddressSearch];
  queryClient.setQueryData(queryKey, [selectedAddress]);
};

/**
 * Apply any validation rules to enable the query for the address search input
 * @param searchValue - The current search value, not the debounced value
 * @returns True if passes all the validation rules, false otherwise
 */
export const shouldEnableQuery = (searchValue: string) => {
  //TODO: Add more conditions such as minLength, maxLength, compare previous searchValue with currentSearchValue and ignore if the difference is ' ' character
  return !!searchValue;
};

/**
 * Check if a location has at least one of the new Exeboot NBN plans
 * @param services - The services of the user
 * @param locationId - The location ID of the location
 * @returns True if the location has at least one of the new Exeboot NBN plans, false otherwise
 */
export const hasOnePlanServiceAtLocation = ({
  services,
  locationId,
}: {
  services: UserService;
  locationId: string;
}) => {
  const foundService = services.find(
    (service) =>
      service.serviceType === 'BROADBAND' &&
      EXEBOOT_BROADBAND_PLAN_IDS.includes(service.offer.id) &&
      service.service.locationId === locationId
  );
  return !!foundService;
};

/**
 * Check if a location is associated with NBN
 * @param address - The address of the location
 * @param services - The services of the user
 * @param callback - The callback to call if the location is associated with NBN
 */
export const checkIfLocationAssociateWithNbn = ({
  address,
  services,
  callback,
}: {
  address: AddressMatch | null;
  services: UserService;
  callback: () => void;
}) => {
  if (!address) {
    return;
  }

  const nbnProvider = address.providers.find(
    (provider) => provider.name === 'NBN'
  );
  if (!nbnProvider) {
    return;
  }

  if (
    hasOnePlanServiceAtLocation({
      locationId: nbnProvider.locationId,
      services: services,
    })
  ) {
    callback();
  }
};
const fibrePagePriorityKeywords = ['uniti', 'vision', 'vostronet'];

const shouldRedirectToSuperLoopFibrePage = (
  sqPriority: ServiceProviderLowerCase[]
) => fibrePagePriorityKeywords.includes(sqPriority[0]);

export const buildSuperloopUrl = ({
  locationId,
  sqPriority,
}: {
  locationId: string;
  sqPriority: ServiceProviderLowerCase[];
}) => {
  if (shouldRedirectToSuperLoopFibrePage(sqPriority)) {
    return `${SUPERLOOP_URL}/internet/fibre?search=${locationId}`;
  }

  return `${SUPERLOOP_URL}/internet/nbn?search=${locationId}`;
};
